import { useEffect, useRef, useState } from 'react';
import { getRequest, postRequest } from '../../../../../Helper/ApiClient';
import type { BoothPanelProps } from '../panels';
import type { IBoothNewProduct, IVtsExhibitorHighlight } from './types';
import HighlightTile from './HighlightTile/HighlightTile';
import NewProductTile from './NewProductTile/NewProductTile';
import styles from './HighlightsPanel.module.scss?module';

const SKELETON_HIGHLIGHTS = 3;
const SKELETON_PRODUCTS = 4;

/**
 * Highlights & new: the flip tiles of the exhibitor, its new listings with a direct add to the
 * VTS basket and the brand description. Highlights load on mount for the badge. New products
 * load only once the tab has been active: their endpoint grants reward points for seeing them.
 */
export default function HighlightsPanel({
  exhibitor,
  tab,
  active,
  onBadge,
  track,
}: BoothPanelProps) {
  const [wasActive, setWasActive] = useState(active);

  useEffect(() => {
    if (active) {
      setWasActive(true);
    }
  }, [active]);

  // The endpoint answers false for an unknown exhibitor, hence the union.
  const highlightsRequest = getRequest<IVtsExhibitorHighlight[] | false>(
    '/api/virtual-trade-show/exhibitor-highlights/' + exhibitor.id
  );
  const newProductsRequest = getRequest<IBoothNewProduct[]>(
    '/api/virtual-trade-show/exhibitors/' + exhibitor.id + '/new-products',
    undefined,
    { enabled: wasActive }
  );
  const { mutate: postHighlightView } = postRequest(
    '/api/virtual-trade-show/view-highlight'
  );
  const reportedViews = useRef(new Set<number>());

  const highlights = Array.isArray(highlightsRequest.data)
    ? highlightsRequest.data
    : [];
  const newProducts = Array.isArray(newProductsRequest.data)
    ? newProductsRequest.data
    : [];
  // A disabled query stays pending, so new products only count as loading once requested.
  const newProductsLoading = wasActive && newProductsRequest.isPending;
  const isLoading = highlightsRequest.isPending || newProductsLoading;
  const isError = highlightsRequest.isError && newProductsRequest.isError;
  const badge = highlightsRequest.isPending
    ? null
    : highlights.length + newProducts.length;

  useEffect(() => {
    onBadge(tab, badge);
  }, [tab, onBadge, badge]);

  // Like the old popup: the first turn of a highlight the user has not seen yet is reported once.
  const handleTurnToBack = (highlight: IVtsExhibitorHighlight) => {
    if (highlight.isViewedByUser || reportedViews.current.has(highlight.id)) {
      return;
    }
    reportedViews.current.add(highlight.id);
    const formData = new FormData();
    formData.append('highlight_id', String(highlight.id));
    postHighlightView(formData);
  };

  if (isLoading) {
    return (
      <div className={styles.panel} aria-busy="true">
        <section className={styles.section}>
          <h2 className={styles.sectionLabel}>Highlights</h2>
          <ul className={styles.highlightGrid} aria-label="Loading highlights">
            {Array.from({ length: SKELETON_HIGHLIGHTS }, (_, index) => (
              <li key={index} className={styles.skeletonHighlight} />
            ))}
          </ul>
        </section>
        <section className={styles.section}>
          <h2 className={styles.sectionLabel}>New products</h2>
          <ul className={styles.productGrid} aria-label="Loading new products">
            {Array.from({ length: SKELETON_PRODUCTS }, (_, index) => (
              <li key={index} className={styles.skeletonProduct} />
            ))}
          </ul>
        </section>
      </div>
    );
  }

  const description = exhibitor.description?.trim() ?? '';

  return (
    <div className={styles.panel}>
      {highlights.length === 0 && newProducts.length === 0 && (
        <p className={styles.message} role={isError ? 'alert' : undefined}>
          {isError
            ? 'Highlights could not be loaded. Please try again later.'
            : 'No highlights yet.'}
        </p>
      )}

      {highlights.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionLabel}>Highlights</h2>
          <ul className={styles.highlightGrid}>
            {highlights.map((highlight, index) => (
              <li key={highlight.id}>
                <HighlightTile
                  highlight={highlight}
                  position={index + 1}
                  brand={exhibitor.name}
                  onTurnToBack={handleTurnToBack}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {newProducts.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionLabel}>New products</h2>
          <ul className={styles.productGrid}>
            {newProducts.map((product) => (
              <li key={product.product_attribute.id}>
                <NewProductTile product={product} track={track} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {description !== '' && (
        <section className={styles.section}>
          <h2 className={styles.sectionLabel}>Why {exhibitor.name}</h2>
          <p className={styles.description}>{description}</p>
        </section>
      )}
    </div>
  );
}
